import {
  Alert,
  Badge,
  Button,
  Card,
  SelectField,
  Spinner,
  TextField,
  useConfirm,
  useToast,
} from '@cartly/ui';
import { Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../api/client';
import { useLoader, useSubmit } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';

interface ProviderInfo {
  own: boolean;
  provider: string | null;
  model: string | null;
  keyLast4: string | null;
  keyUpdatedAt: string | null;
  serverDefault: { provider: string; model: string } | null;
  providers: { id: string; label: string; defaultModel: string }[];
}
const MODEL_HINTS: Record<string, string> = {
  anthropic: 'claude-sonnet-5-5, claude-haiku-5-5',
  openai: 'gpt-4.1-mini, gpt-4.1',
};

function ProviderCard({
  info,
  canManage,
  onChanged,
}: {
  info: ProviderInfo;
  canManage: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const submit = useSubmit();
  const first = info.providers[0]?.id ?? 'anthropic';
  const [provider, setProvider] = useState(info.provider ?? first);
  const [model, setModel] = useState(info.model ?? '');
  const [apiKey, setApiKey] = useState('');
  const current = info.providers.find((p) => p.id === provider);
  const sameProvider = info.own && info.provider === provider;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await submit.run(async () => {
      await api.put('/ai/provider', {
        provider,
        ...(model.trim() ? { model: model.trim() } : {}),
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
      });
      return true;
    });
    if (ok) {
      setApiKey('');
      toast.success('AI provider verified and saved');
      onChanged();
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: 'Remove your AI provider key?',
      message: 'The stored key is deleted. The assistant stops answering until a key is added.',
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete('/ai/provider');
      setApiKey('');
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Card
      title="AI provider"
      subtitle="Your own account with an AI provider. Your key is verified, stored encrypted and never shown again."
      actions={
        info.own ? (
          <Badge tone="success">
            {info.providers.find((p) => p.id === info.provider)?.label ?? info.provider} · key …
            {info.keyLast4}
          </Badge>
        ) : info.serverDefault ? (
          <Badge tone="neutral">Using the platform default</Badge>
        ) : (
          <Badge tone="warning">Not set</Badge>
        )
      }
    >
      {canManage ? (
        <form className="stack" onSubmit={(e) => void onSubmit(e)}>
          {submit.error && <Alert tone="critical">{submit.error}</Alert>}
          <SelectField
            label="Provider"
            value={provider}
            onChange={(e) => {
              setProvider(e.target.value);
              setModel('');
            }}
            options={info.providers.map((p) => ({ value: p.id, label: p.label }))}
          />
          <TextField
            label="Model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder={current?.defaultModel}
            hint={`Leave empty for ${current?.defaultModel ?? 'the default'}. Examples: ${MODEL_HINTS[provider] ?? ''}`}
            autoComplete="off"
          />
          <TextField
            label="API key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={sameProvider ? `Saved key …${info.keyLast4 ?? ''}` : undefined}
            hint={
              sameProvider
                ? 'Leave empty to keep the saved key (for example to change only the model).'
                : 'Create one in your provider’s console. It is checked with a tiny test request when you save.'
            }
            autoComplete="off"
            required={!sameProvider}
          />
          <div className="row">
            <Button type="submit" variant="primary" loading={submit.pending}>
              Verify and save
            </Button>
            {info.own && (
              <Button type="button" icon={<Trash2 size={14} />} onClick={() => void remove()}>
                Remove key
              </Button>
            )}
          </div>
        </form>
      ) : (
        <p className="text-muted">You need the “manage AI” permission to change the provider.</p>
      )}
    </Card>
  );
}

/** Settings → AI provider: the organization's own OpenAI / Anthropic key for the assistant. */
export function AiProviderSettings() {
  const session = useActiveSession();
  const { can } = useSession();
  const tenantId = session.activeTenant?.id;
  const provider = useLoader(() => api.get<ProviderInfo>('/ai/provider'), tenantId);
  if (provider.loading && !provider.data) return <Spinner size="lg" label="Loading" />;
  if (provider.error || !provider.data) {
    return <Alert tone="critical">{provider.error ?? 'Failed to load'}</Alert>;
  }
  return (
    <ProviderCard
      info={provider.data}
      canManage={can('ai:manage')}
      onChanged={() => void provider.reload()}
    />
  );
}
